import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface StructuredResponseProps {
  content: string;
  isStructured: boolean;
}

export const StructuredResponse: React.FC<StructuredResponseProps> = ({
  content,
  isStructured
}) => {
  // Check if content has markdown headers (### format)
  const hasMarkdownHeaders = content.includes('### ');
  
  if (!isStructured && !hasMarkdownHeaders) {
    return (
      <div className="eco-markdown prose prose-sm max-w-none leading-relaxed">
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={{
            h3: ({children}) => <h3 className="text-lg font-semibold mt-6 mb-2 text-primary border-b border-border pb-1">{children}</h3>,
            ul: ({children}) => <ul className="list-disc ml-4 mt-2 space-y-1">{children}</ul>,
            li: ({children}) => <li className="text-sm leading-relaxed">{children}</li>,
            p: ({children}) => <p className="mb-3 leading-relaxed">{children}</p>,
            strong: ({children}) => <strong className="font-semibold text-primary">{children}</strong>,
            table: ({children}) => (
              <div className="my-4 overflow-x-auto">
                <Table className="w-full">
                  {children}
                </Table>
              </div>
            ),
            thead: ({children}) => <TableHeader>{children}</TableHeader>,
            tbody: ({children}) => <TableBody>{children}</TableBody>,
            tr: ({children}) => <TableRow>{children}</TableRow>,
            th: ({children}) => <TableHead className="text-left font-medium">{children}</TableHead>,
            td: ({children}) => <TableCell>{children}</TableCell>
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  }

  // Handle new markdown format
  if (hasMarkdownHeaders) {
    const sections = content.split(/(?=### )/g).filter(section => section.trim());
    
    return (
      <div className="space-y-6">
        {sections.map((section, index) => {
          const trimmedSection = section.trim();
          if (!trimmedSection) return null;

          const lines = trimmedSection.split('\n');
          const headerLine = lines[0];
          const sectionContent = lines.slice(1).join('\n').trim();
          
          // Extract the header text (remove ###)
          const headerText = headerLine.replace(/^### /, '');

          return (
            <Card key={index} className="bg-gradient-to-r from-background to-muted/20 border-l-4 border-l-primary shadow-sm rounded-xl p-6">
              <div className="space-y-4">
                <h4 className="font-medium text-primary flex items-center gap-2 text-lg">
                  {headerText}
                </h4>
                {sectionContent && (
                  <>
                    <Separator className="opacity-20" />
                    <div className="eco-markdown prose prose-sm max-w-none text-foreground leading-relaxed">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          ul: ({children}) => <ul className="list-disc ml-4 mt-2 space-y-2">{children}</ul>,
                          li: ({children}) => <li className="text-sm leading-relaxed">{children}</li>,
                          p: ({children}) => <p className="mb-3 leading-relaxed">{children}</p>,
                          strong: ({children}) => <strong className="font-semibold text-primary">{children}</strong>,
                          table: ({children}) => (
                            <div className="my-4 overflow-x-auto">
                              <Table className="w-full">
                                {children}
                              </Table>
                            </div>
                          ),
                          thead: ({children}) => <TableHeader>{children}</TableHeader>,
                          tbody: ({children}) => <TableBody>{children}</TableBody>,
                          tr: ({children}) => <TableRow>{children}</TableRow>,
                          th: ({children}) => <TableHead className="text-left font-medium">{children}</TableHead>,
                          td: ({children}) => <TableCell>{children}</TableCell>
                        }}
                      >
                        {sectionContent}
                      </ReactMarkdown>
                    </div>
                  </>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    );
  }

  // Legacy emoji-based format (fallback)
  const blocks = content.split(/(?=💡|📌|🧠|⚡|📍|✅|❓)/).filter(block => block.trim());

  if (blocks.length <= 1) {
    return (
      <div className="eco-markdown prose prose-sm max-w-none">
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={{
            strong: ({children}) => <strong className="font-semibold text-primary">{children}</strong>,
            table: ({children}) => (
              <div className="my-4 overflow-x-auto">
                <Table className="w-full">
                  {children}
                </Table>
              </div>
            ),
            thead: ({children}) => <TableHeader>{children}</TableHeader>,
            tbody: ({children}) => <TableBody>{children}</TableBody>,
            tr: ({children}) => <TableRow>{children}</TableRow>,
            th: ({children}) => <TableHead className="text-left font-medium">{children}</TableHead>,
            td: ({children}) => <TableCell>{children}</TableCell>
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {blocks.map((block, index) => {
        const trimmedBlock = block.trim();
        if (!trimmedBlock) return null;

        const lines = trimmedBlock.split('\n');
        const titleLine = lines[0];
        const contentLines = lines.slice(1).join('\n').trim();

        return (
          <Card key={index} className="bg-gradient-to-r from-background to-muted/20 border-l-4 border-l-primary shadow-sm rounded-xl p-6">
            <div className="space-y-4">
              <h4 className="font-medium text-primary flex items-center gap-2 text-lg">
                {titleLine}
              </h4>
              {contentLines && (
                <>
                  <Separator className="opacity-20" />
                  <div className="eco-markdown prose prose-sm max-w-none text-foreground leading-relaxed">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        ul: ({children}) => <ul className="list-disc ml-4 mt-2 space-y-2">{children}</ul>,
                        li: ({children}) => <li className="text-sm leading-relaxed">{children}</li>,
                        p: ({children}) => <p className="mb-3 leading-relaxed">{children}</p>,
                        strong: ({children}) => <strong className="font-semibold text-primary">{children}</strong>,
                        table: ({children}) => (
                          <div className="my-4 overflow-x-auto">
                            <Table className="w-full">
                              {children}
                            </Table>
                          </div>
                        ),
                        thead: ({children}) => <TableHeader>{children}</TableHeader>,
                        tbody: ({children}) => <TableBody>{children}</TableBody>,
                        tr: ({children}) => <TableRow>{children}</TableRow>,
                        th: ({children}) => <TableHead className="text-left font-medium">{children}</TableHead>,
                        td: ({children}) => <TableCell>{children}</TableCell>
                      }}
                    >
                      {contentLines}
                    </ReactMarkdown>
                  </div>
                </>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
};